import Header from '@/components/Header';
import useDocumentTitle from '@/components/Helmet';
import { useCustomLoader } from '@/components/Loader';
import { toast, useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance';
import { formatPrice, formatTableDate } from '@/lib/utils';
import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';

export default function SellerPayments() {
    useDocumentTitle("Payments");
    const loader = useCustomLoader();
    const { toast } = useToast();
    const isMobile = useMediaQuery("(max-width: 600px)");


    const [items, setItems] = useState<any>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [expanded, setExpanded] = React.useState<string | false>(false);


    const fetchItems = async () => {
        loader(true);
        try {
            const { data } = await axiosInstance.get('/api/seller/payments', {
                params: {
                    page,
                    limit
                }
            });
            setItems(data.items);
            setCount(data.count);
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
        finally {
            loader(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [page, limit]);

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Paper elevation={0} sx={{ p: 2 }}>
                    <Typography variant="h6">My Payments</Typography>
                    <TableContainer style={{ overflowX: 'auto' }}>
                        {
                            isMobile ? <Box>
                                {
                                    items?.map((row: any, index: any) => (
                                        <Accordion
                                            expanded={expanded === row._id}
                                            key={row._id}
                                            onChange={() => setExpanded(expanded === row._id ? null : row._id)}
                                            className='mb-2'
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMore />}
                                                aria-controls="panel1-content"
                                                id="panel1-header"
                                                sx={{
                                                    "& .MuiAccordionSummary-content": {
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    },
                                                    backgroundColor: "#f9fafb",
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                                                        {row.plan?.code}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={row.status}
                                                    size="small"
                                                    sx={{ ml: "auto", width: 100, height: 30, textTransform: "capitalize" }}
                                                    color={row.status ? "success" : "error"}
                                                />
                                            </AccordionSummary>

                                            <AccordionDetails>
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <span >Amount:</span><br />
                                                        <span className="font-semibold break-words overflow-hidden text-ellipsis block"> 
                                                            {formatPrice(row.amountInPaise / 100)}
                                                        </span>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <span >Payment Date:</span><br />
                                                        <span className="font-semibold break-words overflow-hidden text-ellipsis block"> 
                                                            {formatTableDate(row.createdAt)}
                                                        </span>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <span >Order ID:</span><br />
                                                        <span className="font-semibold break-words overflow-hidden text-ellipsis block"> 
                                                            {row.orderId}
                                                        </span>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <span >Payment ID:</span><br />
                                                        <span className="font-semibold break-words overflow-hidden text-ellipsis block"> 
                                                            {row.paymentId}
                                                        </span>
                                                    </Grid>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                            </Box> :
                                <Table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Pymt. Date</TableCell>
                                            <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Plan</TableCell>
                                            <TableCell style={{ textAlign: 'right', borderBottom: '1px solid #ddd', padding: 8 }}>Amount</TableCell>
                                            <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Status</TableCell>
                                            <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Order ID</TableCell>
                                            <TableCell style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 8 }}>Payment ID</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((row: any) => (
                                            <TableRow key={row._id}>
                                                <TableCell style={{ padding: 8 }}>{formatTableDate(row.createdAt)}</TableCell>
                                                <TableCell style={{ padding: 8 }}>{row.plan?.code}</TableCell>
                                                <TableCell style={{ padding: 8, textAlign: 'right' }}>{formatPrice(row.amountInPaise / 100)}</TableCell>
                                                <TableCell style={{ padding: 8 }}>
                                                    <Chip
                                                        label={row.status}
                                                        color="success"
                                                        sx={{ textTransform: 'capitalize' }}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ padding: 8 }}>{row.orderId}</TableCell>
                                                <TableCell style={{ padding: 8 }}>{row.paymentId || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                        {items.length === 0 && (
                                            <TableRow><TableCell colSpan={6} style={{ textAlign: 'center' }}>No payments yet</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>}
                        <TablePagination
                            component="div"
                            count={count}
                            page={page - 1}
                            onPageChange={(event, newPage) => setPage(newPage + 1)}
                            rowsPerPage={limit}
                            onRowsPerPageChange={(event) => setLimit(parseInt(event.target.value))}
                        />
                    </TableContainer>
                </Paper>
            </section>
        </div>
    );
}
